"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useAuthState } from "@/hooks/useAuthState";
import { ticketBackHref } from "@/app/components/support/supportTicketBack";

type ContextValue = {
  backHref: string;
  setBackHref: (href: string) => void;
};

const SupportTicketBackContext = createContext<ContextValue | null>(null);

export function SupportTicketBackProvider({ children }: { children: ReactNode }) {
  const { isLoggedIn } = useAuthState();
  const defaultHref = ticketBackHref(isLoggedIn, undefined);
  const [backHref, setBackHrefState] = useState(defaultHref);

  const setBackHref = useCallback((href: string) => {
    setBackHrefState(href);
  }, []);

  useEffect(() => {
    setBackHrefState(ticketBackHref(isLoggedIn, undefined));
  }, [isLoggedIn]);

  const value = useMemo(
    () => ({ backHref, setBackHref }),
    [backHref, setBackHref],
  );

  return (
    <SupportTicketBackContext.Provider value={value}>{children}</SupportTicketBackContext.Provider>
  );
}

export function useSupportTicketBackHref(): ContextValue {
  const ctx = useContext(SupportTicketBackContext);
  if (!ctx) {
    throw new Error("useSupportTicketBackHref must be used within SupportTicketBackProvider");
  }
  return ctx;
}
